import { UserFriendlyError } from '@affine/error';
import {
  gqlFetcherFactory,
  type GraphQLQuery,
  type QueryOptions,
  type QueryResponse,
} from '@affine/graphql';
import { fromPromise, Service } from '@toeverything/infra';
import type { Observable } from 'rxjs';

import { AuthService } from './auth';
import type { FetchService } from './fetch';
import { PaywallService } from './paywall';

/** Server error names that should trigger the Curiescious paywall modal. */
const PAYWALL_ERROR_NAMES = new Set([
  'analyze_quota_exceeded',
  'deep_research_quota_exceeded',
  'workspace_quota_exceeded',
]);

export class GraphQLService extends Service {
  constructor(private readonly fetcher: FetchService) {
    super();
  }

  private readonly rawGql = gqlFetcherFactory('/graphql', this.fetcher.fetch);

  rxGql = <Query extends GraphQLQuery>(
    options: QueryOptions<Query>
  ): Observable<QueryResponse<Query>> => {
    return fromPromise(signal => {
      return this.gql({
        ...options,
        context: {
          signal,
          ...options.context,
        },
      } as any);
    });
  };

  gql = async <Query extends GraphQLQuery>(
    options: QueryOptions<Query>
  ): Promise<QueryResponse<Query>> => {
    try {
      return await this.rawGql(options);
    } catch (anyError) {
      const error = UserFriendlyError.fromAny(anyError);

      if (error.isStatus(401)) {
        this.framework.get(AuthService).session.revalidate();
      }

      // Curiescious paywall trigger: when the server returns a per-plan
      // quota-exceeded error, intercept and open the paywall modal.
      const errorName = (error as { name?: string }).name;
      if (errorName && PAYWALL_ERROR_NAMES.has(errorName)) {
        const data =
          ((error as { data?: Record<string, unknown> }).data ?? {}) as Record<
            string,
            unknown
          >;
        try {
          this.framework.get(PaywallService).show({
            feature: errorName.replace(/_quota_exceeded$/, ''),
            used: Number(data.used ?? 0),
            limit: Number(data.limit ?? 0),
            requiredPlan: String(data.requiredPlan ?? 'researcher'),
          });
        } catch {
          // PaywallService not in scope — ignore.
        }
      }

      throw error;
    }
  };
}
